import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BED_RANGES = ["Clinic / OPD only", "10–50 beds", "50–150 beds", "150–500 beds", "500+ beds"];

export default function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", organization: "", role: "", bed_count: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.organization) {
      toast.error("Please fill name, email and organization");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/demo-requests`, form);
      setDone(true);
      toast.success("Got it — our team will reach out within one business day.");
      setForm({ name: "", email: "", phone: "", organization: "", role: "", bed_count: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Couldn't submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-ab-bg py-24 md:py-36">
      <div className="ab-container grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="ab-eyebrow text-ab-accent">Talk to us</p>
          <h2 className="ab-h2 text-5xl md:text-6xl mt-4">
            Let's make your hospital <span className="italic font-light">breathe</span>.
          </h2>
          <p className="mt-6 text-ab-muted leading-relaxed max-w-md">
            Tell us a little about your setup. A founder will get back within one business day with a tailored 30-minute demo.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-ab-text/85">
            <li className="flex items-center gap-3"><CheckCircle2 className="w-4 h-4 text-ab-accent" /> 7-day onboarding for clinics</li>
            <li className="flex items-center gap-3"><CheckCircle2 className="w-4 h-4 text-ab-accent" /> ABDM-ready, CERT-IN audited</li>
            <li className="flex items-center gap-3"><CheckCircle2 className="w-4 h-4 text-ab-accent" /> Migration from existing HMS supported</li>
          </ul>
        </div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="lg:col-span-7 bg-white border border-ab-border rounded-3xl p-6 md:p-10"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Your name" required>
              <input data-testid="contact-name" required value={form.name} onChange={update("name")} className="ab-input" placeholder="Dr. Aisha Khan" />
            </Field>
            <Field label="Work email" required>
              <input data-testid="contact-email" required type="email" value={form.email} onChange={update("email")} className="ab-input" placeholder="aisha@hospital.com" />
            </Field>
            <Field label="Phone">
              <input data-testid="contact-phone" value={form.phone} onChange={update("phone")} className="ab-input" placeholder="+91 98xxxxxxxx" />
            </Field>
            <Field label="Organization" required>
              <input data-testid="contact-org" required value={form.organization} onChange={update("organization")} className="ab-input" placeholder="Lifeline Hospitals" />
            </Field>
            <Field label="Your role">
              <input data-testid="contact-role" value={form.role} onChange={update("role")} className="ab-input" placeholder="Medical Superintendent" />
            </Field>
            <Field label="Bed range">
              <select data-testid="contact-beds" value={form.bed_count} onChange={update("bed_count")} className="ab-input">
                <option value="">Select…</option>
                {BED_RANGES.map((b) => <option key={b}>{b}</option>)}
              </select>
            </Field>
            <div className="md:col-span-2">
              <Field label="What would you like to fix first?">
                <textarea data-testid="contact-message" rows={4} value={form.message} onChange={update("message")} className="ab-input min-h-[120px]" placeholder="OPD queues, IPD billing, discharge time, ABDM linkage…" />
              </Field>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
            <p className="text-xs text-ab-muted max-w-sm">By submitting, you agree to our privacy policy. We don't share your data — ever.</p>
            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-form-submit-btn"
              className="ab-pill-btn group disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending</>) : done ? (<><CheckCircle2 className="w-4 h-4" /> Sent</>) : (<>Book my demo <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></>)}
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="ab-eyebrow text-ab-muted block mb-2">{label}{required && <span className="text-ab-accent ml-1">*</span>}</span>
      {children}
    </label>
  );
}

// inject input style once
if (typeof document !== "undefined" && !document.getElementById("ab-input-style")) {
  const style = document.createElement("style");
  style.id = "ab-input-style";
  style.innerHTML = `.ab-input{ width:100%; background:#F6F5F2; border:1px solid #E8E6E1; border-radius:14px; padding:14px 16px; font-family:'Outfit',sans-serif; color:#121212; transition: border-color .2s ease, box-shadow .2s ease, background .2s ease; outline:none;} .ab-input:focus{ border-color:#0D4035; box-shadow:0 0 0 4px rgba(0,135,103,0.12); background:#fff;} .ab-input::placeholder{ color:#9c9b95; }`;
  document.head.appendChild(style);
}
